import { parsePhoneNumber, validatePhoneNumber } from '@/helper/phoneUtils';
import { showToast } from '@/helper/toast.helper';
import { desanitizeKey } from '@/helper/utils';
import { AppDispatch } from '@/store';
import { fetchBlueNumberDetails } from '@/store/slices/adminUsersSlice/adminUserSlice';
import { AdminUser } from '@/types/project.types';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface RoleAssignmentProps {
  admins: AdminUser[];
  onAdminChange: (index: number, field: keyof AdminUser, value: string | number) => void;
  addAdmin: () => void;
  removeAdmin: (index: number) => void;
  onValidationChange?: (isValid: boolean) => void;
  isEditMode: boolean;
}

const RoleAssignment: React.FC<RoleAssignmentProps> = ({
  admins,
  onAdminChange,
  addAdmin,
  onValidationChange,
  removeAdmin,
  isEditMode,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [error, setError] = useState<Record<number, Record<string, string>>>({});
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState<Record<number, boolean>>({});
  const [localInputs, setLocalInputs] = useState<
    Record<number, { phone: string; blueNumber: string }>
  >({});

  const roles = useSelector((state: any) => state.user.roles);

  const debounceTimers = useRef<Record<number, NodeJS.Timeout>>({});
  const dropdownRef = useRef<Record<number, HTMLDivElement | null>>({});
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    validateAllAdmins();
  }, [admins, error, localInputs]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openIndex !== null) {
        const ref = dropdownRef.current[openIndex];
        if (ref && !ref.contains(e.target as Node)) {
          setOpenIndex(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openIndex]);

  const validateAllAdmins = () => {
    // const hasErrors = Object.values(error).some((e) => Object.keys(e).length > 0);

    const hasErrors = Object.values(error).some((e) =>
      Object.values(e).some((msg) => msg && msg.length > 0),
    );

    const allFilled = admins.every((admin, index) => {
      const local = localInputs[index] || {};
      return (
        (local.phone ?? admin.phoneNumber) &&
        (local.blueNumber ?? admin.blueNumberId) &&
        admin.roleId
      );
    });

    const isValid = !hasErrors && allFilled;
    if (onValidationChange) onValidationChange(isValid);
    return isValid;
  };

  const handleMobileValidation = (value: string) => {
    let isValid = false;
    if (value) {
      try {
        const parsed = parsePhoneNumber(value);
        const data = validatePhoneNumber(parsed.phoneNumber, parsed.countryCode);
        isValid = data.isValid;
      } catch (error) {
        isValid = false;
      }
    }
    return isValid;
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    setLoading((prev) => ({ ...prev, [index]: true }));

    // Update local input immediately for responsive typing
    setLocalInputs((prev) => ({
      ...prev,
      [index]: { ...prev[index], phone: value },
    }));

    const validatedPhoneNumber = handleMobileValidation(value);
    let newError: Record<string, string> = {};

    // Frontend validation
    if (!/^[+0-9]*$/.test(value)) newError.phoneNumber = 'Invalid characters in phone number';
    else if ((value.match(/\+/g) || []).length > 1)
      newError.phoneNumber = 'Invalid phone number format';
    else if (value.includes('+') && value.indexOf('+') !== 0)
      newError.phoneNumber = "'+' sign is only allowed at the beginning";
    else if (value && !validatedPhoneNumber)
      newError.phoneNumber = 'Please enter a valid phone number with country code';

    // Update error state
    setError((prev) => ({
      ...prev,
      [index]: newError,
    }));
    onAdminChange(index, 'phoneNumber', value);
    const noError = Object.keys(newError).length === 0;
    // If there is any validation error, clear BlueNumber
    if (!noError) {
      onAdminChange(index, 'blueNumberId', '');
      setLocalInputs((prev) => ({
        ...prev,
        [index]: { ...prev[index], blueNumber: '' },
      }));
      return; // stop further API call
    }

    // Check for duplicate phone number before API call
    if (validatedPhoneNumber && noError) {
      const isDuplicate = admins.some((admin, i) => {
        if (i === index) return false;
        return admin.phoneNumber === value;
      });

      if (isDuplicate) {
        setError((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            phoneNumber: 'This phone number is already added in this Project.',
          },
        }));
        onAdminChange(index, 'blueNumberId', '');
        setLocalInputs((prev) => ({
          ...prev,
          [index]: { ...prev[index], blueNumber: '' },
        }));
        setLoading((prev) => ({ ...prev, [index]: false }));
        return;
      }

      if (!debounceTimers.current) debounceTimers.current = {};
      if (debounceTimers.current[index]) clearTimeout(debounceTimers.current[index]);

      debounceTimers.current[index] = setTimeout(async () => {
        try {
          const parsed = parsePhoneNumber(value);
          const res = await dispatch(fetchBlueNumberDetails(parsed.fullNumber));

          if (fetchBlueNumberDetails.fulfilled.match(res)) {
            const result = res.payload?.blueNumber;
            setLoading((prev) => ({ ...prev, [index]: false }));

            if (!result) {
              onAdminChange(index, 'blueNumberId', '');
              setLocalInputs((prev) => ({
                ...prev,
                [index]: { ...prev[index], blueNumber: '' },
              }));
              return;
            }
            const id = res.payload?.blueNumber || '';

            // Update parent and local state
            onAdminChange(index, 'blueNumberId', id);
            setLocalInputs((prev) => ({
              ...prev,
              [index]: { ...prev[index], blueNumber: id },
            }));

            // Show backend message if present
            if (res.payload?.blueNumber) {
              showToast.success('User added successfully');
            }
          } else if (fetchBlueNumberDetails.rejected.match(res)) {
            setLoading((prev) => ({ ...prev, [index]: false }));

            const errorMessage =
              (res.payload as any)?.message ||
              (res.error as any)?.message ||
              'Error fetching BlueNumber';

            onAdminChange(index, 'blueNumberId', '');
            setLocalInputs((prev) => ({
              ...prev,
              [index]: { ...prev[index], blueNumber: '' },
            }));
            // showToast.error(errorMessage);
          }
        } catch (err: any) {
          onAdminChange(index, 'blueNumberId', '');
          setLocalInputs((prev) => ({
            ...prev,
            [index]: { ...prev[index], blueNumber: '' },
          }));
          showToast.error(err?.message || 'Error fetching BlueNumber');
        } finally {
          setLoading((prev) => ({ ...prev, [index]: false }));
        }
      }, 500);
    }
  };

  const roleColors = [
    'bg-[#E86A9BCC] text-[#4A082D]',
    'bg-[#4C86E7CC] text-[#4A082D]',
    'bg-[#5CC9A7CC] text-[#4A082D]',
  ];

  const getRoleColor = (index: number) => {
    return roleColors[index % roleColors.length];
  };

  return (
    <div className="mx-auto text-[#621C43]">
      {/* Header */}
      {/* Admin Fields */}
      <div className="mx-auto text-[#621C43]">
        <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
          <h2 className="text-xl font-bold">Assign Users to Project</h2>
          <p className="text-base font-medium">
            Add users to manage the {isEditMode ? '' : 'new '}project
          </p>
        </div>

        {/* GRID OF ADMIN CARDS */}
        <div className="grid md:grid-cols-3 gap-6 p-5">
          {admins.map((admin, index) => (
            <div key={index} className="relative rounded-xl border p-4 bg-white shadow">
              {index !== 0 && (
                <button
                  onClick={() => removeAdmin(index)}
                  className="absolute top-2 right-6 text-lg font-bold"
                >
                  ×
                </button>
              )}

              {/* TITLE */}
              <div className="flex items-center gap-1 mb-2">
                <p className="font-semibold">Admin {index + 1}</p>
                <span className="text-red-500">*</span>
              </div>

              {/* ROLE */}
              <div
                ref={(el) => {
                  dropdownRef.current[index] = el; // assign the DOM element
                }}
                className="relative w-full mb-4"
              >
                <label className="text-xs italic font-medium">
                  Choose Role <span className="text-red-500">*</span>
                </label>
                <button
                  onClick={() => setOpenIndex(index === openIndex ? null : index)}
                  className={`w-full p-2 italic font-medium rounded-md flex justify-between items-center ${
                    admins[index]?.roleId ? getRoleColor(index) : 'bg-[#4A082D33] text-[#621C43]'
                  }`}
                >
                  <span>
                    {admins[index].roleId
                      ? desanitizeKey(
                          roles.find((r: any) => r.id === admins[index].roleId)?.name || '',
                        )
                      : 'Choose Role'}
                  </span>
                  <span className="ml-2">
                    {openIndex === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                </button>

                {openIndex === index && (
                  <ul className="w-full max-h-60 overflow-auto p-2 bg-[#4A082D33] rounded-md mt-1 space-y-1">
                    {roles
                      .filter((role: any) => role.name !== 'ADMIN' && role.name !== 'SUPER_ADMIN')
                      .map((role: any, idx: number) => (
                        <li
                          key={role.id}
                          onClick={() => {
                            onAdminChange(index, 'roleId', role.id);
                            setOpenIndex(null);
                          }}
                          className={`px-3 py-1 text-sm font-medium cursor-pointer rounded-md hover:bg-[#621C43] ${getRoleColor(
                            idx,
                          )} hover:text-white`}
                        >
                          {desanitizeKey(role.name)} — {role.description}
                        </li>
                      ))}
                  </ul>
                )}
              </div>

              {/* MOBILE NUMBER */}
              <label className="text-sm">Enter Mobile Number</label>
              <div className="relative">
                <input
                  type="text"
                  value={localInputs[index]?.phone ?? admins[index].phoneNumber ?? ''}
                  onChange={(e) => handleMobileChange(e, index)}
                  placeholder="+91-xxxxx-xxxxx"
                  className="w-full rounded px-3 py-2 bg-[#4A082D33] text-[#621C43]"
                />
                {loading[index] && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pb-5">
                    <Loader2 className="w-4 h-4 animate-spin text-[#621C43]" />
                  </div>
                )}
                <p className="text-xs text-red-500 mt-2 min-h-[16px]">
                  {error[index]?.phoneNumber || ''}
                </p>
              </div>

              {/* BLUE NUMBER               No need to display Blue Number field as per new requirements (By Harish)      */}

              {/* <label className="text-sm"> User Name</label>
              <input
                type="text"
                disabled
                value={localInputs[index]?.blueNumber ?? admins[index].blueNumberId ?? ''}
                placeholder="Enter User Name"
                className="w-full rounded px-3 py-2 bg-[#4A082D33] text-[#621C43] mb-1 cursor-not-allowed"
              />

              <p className={`text-xs mt-1 ${error[index]?.blueNumber ? 'text-red-500' : 'hidden'}`}>
                {error[index]?.blueNumber}
              </p> */}
            </div>
          ))}

          {/* + ADD ADMIN BUTTON */}
          <button
            onClick={addAdmin}
            className="rounded-lg border border-pink-500 text-pink-600 p-3 hover:bg-pink-50"
          >
            + Add more admins
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignment;
